import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { EmptyState } from '../ui/EmptyState';
import RouteGateFallback from '../ui/RouteGateFallback';
import { Shield } from 'lucide-react';

interface PermissionProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission: 'dashboard' | 'properties' | 'tenants' | 'payments' | 'reports' | 'settings' | 'analytics';
  fallbackPath?: string;
}

/**
 * Route protection component that checks if user has permission to access a page
 * If no permission, shows access denied message or redirects
 */
export const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({
  children,
  requiredPermission,
  fallbackPath = '/',
}) => {
  const location = useLocation();
  const { canAccess, isLoading } = usePermissions();

  if (isLoading) {
    return <RouteGateFallback variant="permissions" />;
  }

  // Check if user has required permission
  if (!canAccess(requiredPermission)) {
    // Redirect to fallback path or show access denied
    if (fallbackPath) {
      return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }

    // Show access denied message with more helpful information
    const pageNames: Record<string, string> = {
      dashboard: 'Dashboard',
      properties: 'Properties',
      tenants: 'Tenants',
      payments: 'Payments',
      reports: 'Reports',
      settings: 'Settings',
      analytics: 'Analytics',
    };
    
    const pageName = pageNames[requiredPermission] || requiredPermission;
    const permissionKey = `canAccess${pageName.charAt(0).toUpperCase() + pageName.slice(1)}`;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: 24 }}>
        <EmptyState
          type="errors"
          title="Access Denied"
          description={`You don't have permission to access the ${pageName} page. This requires the "${permissionKey}" permission. Please contact your administrator to grant this permission.`}
          size="lg"
          icon={Shield}
        />
      </div>
    );
  }

  return <>{children}</>;
};
